import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useResetPassword } from '@uturn/api/v1';
import { getApiErrorMessage } from '@uturn/portal/utils';
import {
	Button,
	cn,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	sonner,
} from '@uturn/ui-kit';
import { isAxiosError } from 'axios';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	type PasswordResetRequestFormValues,
	passwordResetRequestSchema,
} from '../schema';

export function PasswordResetRequestForm() {
	const form = useForm<PasswordResetRequestFormValues>({
		resolver: zodResolver(passwordResetRequestSchema),
		defaultValues: {
			email: '',
		},
	});
	const { mutateAsync, isSuccess } = useResetPassword();
	const { t } = useTranslation();

	const onSubmit: SubmitHandler<PasswordResetRequestFormValues> = async ({
		email,
	}) => {
		try {
			await mutateAsync({ data: { email: email.toLowerCase().trim() } });
		} catch (error) {
			if (isAxiosError(error) && error.response?.status === 400) {
				sonner.error(
					t(
						'pages.auth.password_reset_request.errors.user_not_found',
						'No account found for this email address.',
					),
				);
				return;
			}

			sonner.error(
				getApiErrorMessage(error) ??
					t('general.errors.generic_title', 'Uh oh! Something went wrong.'),
			);
		}
	};

	return (
		<div className={cn('grid gap-6')}>
			{isSuccess ? (
				<p className="text-sm text-center">
					{t('pages.auth.password_reset_request.request_completed', {
						email: form.getValues('email'),
					})}
				</p>
			) : (
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="grid gap-2">
							<div className="grid gap-1">
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="sr-only">
												{t('general.email', 'E-mail')}
											</FormLabel>
											<FormControl>
												<Input
													type="email"
													autoComplete="email"
													autoCorrect="off"
													disabled={form.formState.isSubmitting}
													placeholder="john_doe@example.com"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button type="submit" disabled={form.formState.isSubmitting}>
								{form.formState.isSubmitting && (
									<Icon spin className="mr-3" icon={faSpinnerThird} />
								)}
								{t('pages.auth.password-reset.form.submit', 'Reset password')}
							</Button>
						</div>
					</form>
				</Form>
			)}
		</div>
	);
}
